import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Button from '../../../components/common/Button';
import GradientButton from '../../../components/common/GradientButton';
import Input from '../../../components/common/Input';
import Select from '../../../components/common/Select';
import Pagination from '../../../components/common/Pagination';
import StatusBadge from '../../../components/common/StatusBadge';

import {
  getTransferRequestsApi,
  createTransferRequestApi,
  cancelTransferRequestApi,
} from '../../../api/transferRequest.api';
import { lookupPlayerNationalCodeApi } from '../../../api/player.api';
import {
  TRANSFER_TYPE_LABELS,
  TRANSFER_REQUEST_STATUS_LABELS,
  TRANSFER_REQUEST_STATUS_BADGE,
} from '../../../utils/constants';
import { toJalali } from '../../../utils/jalali';
import {
  sanitizeNationalCode,
  isValidNationalCode,
} from '../../../utils/nationalCode';

export default function ClubTransferRequests() {
  const location = useLocation();
  const navigate = useNavigate();

  const [form, setForm] = useState({ nationalCode: '', type: 'PERMANENT', note: '' });
  const [lookup, setLookup] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: '', type: 'success' });

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });

  // ورود از فرم ثبت بازیکن (کد ملی تکراری): فرم با همان کد پر می‌شود
  useEffect(() => {
    const code = location.state?.nationalCode;
    if (code) {
      setForm((prev) => ({ ...prev, nationalCode: sanitizeNationalCode(code) }));
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const load = useCallback(() => {
    setLoading(true);
    getTransferRequestsApi({ page: pagination.page, limit: pagination.limit })
      .then(({ data }) => {
        setRequests(data?.data || []);
        setPagination((prev) => ({
          ...prev,
          total: data?.meta?.total ?? 0,
          totalPages: data?.meta?.totalPages ?? 1,
        }));
      })
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    load();
  }, [load]);

  // بررسی لحظه‌ای کد ملی
  useEffect(() => {
    const code = sanitizeNationalCode(form.nationalCode);
    if (code.length !== 10) {
      setLookup(null);
      return undefined;
    }
    if (!isValidNationalCode(code)) {
      setLookup({ invalid: true });
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      lookupPlayerNationalCodeApi(code)
        .then(({ data }) => !cancelled && setLookup(data?.data || null))
        .catch(() => !cancelled && setLookup(null));
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [form.nationalCode]);

  const notify = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: 'success' }), 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createTransferRequestApi({
        nationalCode: sanitizeNationalCode(form.nationalCode),
        type: form.type,
        note: form.note || undefined,
      });
      notify('درخواست انتقال ثبت شد و برای مدیر هیئت ارسال شد');
      setForm({ nationalCode: '', type: 'PERMANENT', note: '' });
      setLookup(null);
      setPagination((prev) => ({ ...prev, page: 1 }));
      load();
    } catch (err) {
      notify(err?.response?.data?.message || 'ثبت درخواست ناموفق بود', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      await cancelTransferRequestApi(id);
      notify('درخواست لغو شد');
      load();
    } catch (err) {
      notify(err?.response?.data?.message || 'لغو درخواست ناموفق بود', 'error');
    }
  };

  const codeHint = () => {
    if (!lookup) return null;
    if (lookup.invalid) return <p className="text-xs font-bold text-red-500">کد ملی معتبر نیست</p>;
    if (!lookup.exists)
      return (
        <p className="text-xs font-bold text-amber-600">
          بازیکنی با این کد ملی ثبت نشده است. اگر بازیکن جدید است، او را از بخش «بازیکنان» ثبت کنید.
        </p>
      );
    if (lookup.sameClub)
      return <p className="text-xs font-bold text-amber-600">این بازیکن همین حالا عضو باشگاه شماست.</p>;
    return (
      <p className="text-xs font-bold text-emerald-600">
        ✓ بازیکن پیدا شد؛ عضو فعلی باشگاه «{lookup.clubName}» است.
      </p>
    );
  };

  const canSubmit =
    isValidNationalCode(form.nationalCode) && lookup?.exists && !lookup?.sameClub && !submitting;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-black text-text-dark">درخواست انتقال بازیکن</h1>
        <p className="mt-1 text-sm text-slate-400">
          برای جذب بازیکنی که عضو باشگاه دیگری است، درخواست ثبت کنید. با تأیید مدیر هیئت، انتقال انجام می‌شود.
        </p>
      </div>

      {message.text && (
        <div
          className={`rounded-xl p-3 text-sm font-bold ${
            message.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'
          }`}
        >
          {message.text}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 gap-4 rounded-3xl border border-slate-200 bg-white p-5 md:grid-cols-2"
      >
        <div className="space-y-2">
          <Input
            label="کد ملی بازیکن"
            required
            dir="ltr"
            inputMode="numeric"
            maxLength={10}
            autoComplete="off"
            className="text-left font-mono tracking-widest"
            value={form.nationalCode}
            onChange={(e) => setForm({ ...form, nationalCode: sanitizeNationalCode(e.target.value) })}
          />
          {codeHint()}
        </div>

        <Select
          label="نوع انتقال"
          required
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value })}
        >
          {Object.entries(TRANSFER_TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </Select>

        <div className="md:col-span-2">
          <label className="mb-2 block text-lg font-medium text-text-dark/80">توضیحات (اختیاری)</label>
          <textarea
            rows={3}
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition focus:border-emerald-500"
            placeholder="مثلاً توافق دو باشگاه، مدت قرارداد و ..."
          />
        </div>

        <div className="md:col-span-2 flex justify-end">
          <GradientButton type="submit" disabled={!canSubmit}>
            {submitting ? 'در حال ثبت...' : 'ثبت درخواست انتقال'}
          </GradientButton>
        </div>
      </form>

      <div className="overflow-x-auto rounded-3xl border border-slate-200 bg-white p-4">
        {loading ? (
          <div className="py-10 text-center text-slate-400">در حال بارگذاری...</div>
        ) : (
          <>
            <table className="w-full text-right text-sm">
              <thead className="border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-white">
                <tr>
                  {['بازیکن', 'از باشگاه', 'به باشگاه', 'نوع', 'تاریخ ثبت', 'وضعیت', 'پاسخ مدیر هیئت', ''].map((h) => (
                    <th key={h} className="px-4 py-3 text-xs font-bold text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {requests.map((r) => (
                  <tr key={r.id} className="hover:bg-emerald-50/50">
                    <td className="px-4 py-3 font-bold">{r.player?.firstName} {r.player?.lastName}</td>
                    <td className="px-4 py-3">{r.fromClub?.name || '—'}</td>
                    <td className="px-4 py-3">{r.toClub?.name}</td>
                    <td className="px-4 py-3">{TRANSFER_TYPE_LABELS[r.type] || r.type}</td>
                    <td className="px-4 py-3">{toJalali(r.createdAt)}</td>
                    <td className="px-4 py-3">
                      <StatusBadge
                        status={TRANSFER_REQUEST_STATUS_BADGE[r.status]}
                        label={TRANSFER_REQUEST_STATUS_LABELS[r.status]}
                      />
                    </td>
                    <td className="max-w-[220px] px-4 py-3 text-xs text-slate-500">{r.adminNote || '—'}</td>
                    <td className="px-4 py-3">
                      {r.status === 'PENDING' && (
                        <Button variant="ghost" size="sm" onClick={() => handleCancel(r.id)}>لغو</Button>
                      )}
                    </td>
                  </tr>
                ))}
                {requests.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-slate-400">درخواستی ثبت نشده است</td>
                  </tr>
                )}
              </tbody>
            </table>
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
            />
          </>
        )}
      </div>
    </div>
  );
}
